import React, { useState, useEffect, useCallback } from "react";
import { Button, Row, Col, Spinner } from "@/Components/Common/Tailwind";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { API } from "../../../api";
import { getAccessToken } from "../../../api/auth";
import { LoadingSpinner, EmptyMessage, ItemCard } from "../../../Components/Common";

// B2B item types
export const B2B_TYPES = {
  COUPON: "coupon",
  PRECOUPON: "precoupon",
  VOUCHER: "voucher",
};

// Configuration for each B2B type
const B2B_CONFIG = {
  [B2B_TYPES.COUPON]: {
    apiEndpoint: "/coupon/b2b-stock/",
    getTitle: (item) => item.coupon?.title || item.name,
    getExpireDate: (item) => item.coupon?.expire_date,
    getDiscount: (item) => {
      const coupon = item.coupon;
      if (!coupon) return null;
      return coupon.discount_type === "price"
        ? `${coupon.discount_amount?.toLocaleString()}원 할인쿠폰`
        : `${coupon.discount_rate}% 할인쿠폰`;
    },
    getMinPurchase: (item) => item.coupon?.min_purchase,
    getBoundType: (item) => item.coupon?.bound_type,
    getReservationPeriod: (item) => ({
      start: item.start_date,
      end: item.end_date,
    }),
    emptyMessage: "보낼 B2B 쿠폰이 없습니다.",
    buttonLabel: "쿠폰전송",
    buttonLabelCompleted: "전송완료",
  },
  [B2B_TYPES.PRECOUPON]: {
    apiEndpoint: "/precoupon/b2b-stock/",
    getTitle: (item) => item.name,
    getExpireDate: (item) => item.expire_date,
    getDiscount: () => null,
    getMinPurchase: () => null,
    getBoundType: (item) => item.precoupon?.bound_type,
    getReservationPeriod: (item) => ({
      start: item.precoupon?.reserv_start_date,
      end: item.precoupon?.reserv_end_date,
    }),
    emptyMessage: "보낼 B2B 쿠폰이 없습니다.",
    buttonLabel: "쿠폰전송",
    buttonLabelCompleted: "전송완료",
  },
  [B2B_TYPES.VOUCHER]: {
    apiEndpoint: "/voucher/b2b-stock/",
    getTitle: (item) => item.name,
    getExpireDate: (item) => item.voucher?.expire_date,
    getDiscount: () => null,
    getMinPurchase: () => null,
    getBoundType: () => null,
    getSiteName: (item) => item.site_name,
    getReservationPeriod: () => null,
    emptyMessage: "보낼 B2B 쿠폰이 없습니다.",
    buttonLabel: "쿠폰전송",
    buttonLabelCompleted: "전송완료",
  },
};

/**
 * Generic B2B List Component
 * Renders a list of B2B items (coupons, precoupons, or vouchers) with gift modal
 * 
 * @param {{
 *   type: 'coupon' | 'precoupon' | 'voucher',
 *   GiftModalComponent: React.ComponentType,
 *   todayOnly?: boolean,
 * }} props
 */
function GenericB2BList({ type, GiftModalComponent, todayOnly = false }) {
  const navigate = useNavigate();
  const config = B2B_CONFIG[type];

  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [giftModal, setGiftModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState({});

  const fetchData = useCallback(() => {
    const accessToken = getAccessToken();
    setIsLoading(true);

    API.get(config.apiEndpoint, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })
      .then((response) => {
        setItems(response.data || []);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [config.apiEndpoint]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenGiftModal = (item) => {
    setSelectedItem(item);
    setGiftModal(true);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (items.length === 0) {
    return <EmptyMessage message={config.emptyMessage} height={150} />;
  }

  return (
    <React.Fragment>
      <Row>
        <Col
          className="col-12 flex flex-wrap justify-center gap-4"
          style={todayOnly && items.length ? { marginBottom: "16px" } : {}}
        >
          {items.map((item) => {
            const title = config.getTitle(item);
            const expireDate = config.getExpireDate(item);
            const discount = config.getDiscount?.(item);
            const minPurchase = config.getMinPurchase?.(item);
            const boundType = config.getBoundType?.(item);
            const reservationPeriod = config.getReservationPeriod?.(item);
            const siteName = config.getSiteName?.(item);
            const hasStock = item.stock > 0;

            return (
              <Col lg={12} xxl={12} md={12} sm={12} xs={12} key={item.id}>
                <ItemCard
                  visible={true}
                  className="w-md-480"
                  style={{
                    backgroundColor: "#fff",
                    opacity: item.downloaded ? 0.5 : 1,
                  }}
                >
                  {/* Header with title and action button */}
                  <div className="flex justify-between px-2 border-bottom items-center">
                    <div
                      className="card-title pt-3"
                      style={{ fontWeight: "600", maxWidth: "65%" }}
                    >
                      {title}
                    </div>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      className="font-bold mt-2 h-auto py-1 whitespace-normal text-center"
                      style={{ opacity: item.downloaded ? 0.5 : 1, cursor: "pointer" }}
                      onClick={() => handleOpenGiftModal(item)}
                    >
                      {hasStock ? config.buttonLabel : config.buttonLabelCompleted}
                      <br />
                      {hasStock && <span>{item.stock}개 전송가능</span>}
                    </Button>
                  </div>

                  {/* Discount info (coupon only) */}
                  {discount && (
                    <div className="flex justify-between px-2 border-bottom">
                      <div
                        className="card-title pt-3"
                        style={{ fontWeight: "600", color: "#f64757" }}
                      >
                        {discount}
                      </div>
                    </div>
                  )}

                  {/* Min purchase info (coupon only) */}
                  {minPurchase && (
                    <div className="flex justify-between px-2 border-bottom">
                      <div
                        className="card-title pt-3"
                        style={{ fontWeight: "400", color: "#5d5d5d" }}
                      >
                        {minPurchase.toLocaleString()}원 이상 구매시 사용가능
                      </div>
                    </div>
                  )}

                  {/* Site name (voucher only) */}
                  {siteName && (
                    <div className="flex justify-between px-2 border-bottom">
                      <div
                        className="card-title pt-3"
                        style={{ fontWeight: "400", color: "#5d5d5d" }}
                      >
                        {siteName}
                      </div>
                    </div>
                  )}

                  {/* Expire date and reservation period */}
                  <div className="flex px-2">
                    <div className="mt-1" style={{ width: "100%" }}>
                      <p className="card-text py-2" style={{ width: "100%" }}>
                        <span style={{ lineHeight: "32px" }}>
                          {type === B2B_TYPES.COUPON ? "배포기간" : "만료기한"}{" "}
                          {expireDate || "없음"}
                        </span>
                      </p>
                      {boundType === "reservation" && reservationPeriod?.start && (
                        <>
                          <p className="card-text mb-1">예약가능기간</p>
                          <p className="card-text pb-3">
                            {reservationPeriod.start} ~ {reservationPeriod.end}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </ItemCard>
              </Col>
            );
          })}
        </Col>
      </Row>

      {GiftModalComponent && (
        <GiftModalComponent
          setVisible={setGiftModal}
          visible={giftModal}
          info={selectedItem}
          setInfo={setSelectedItem}
          navigate={navigate}
          needRefresh={fetchData}
        />
      )}
    </React.Fragment>
  );
}

export default GenericB2BList;
